import { generateFormCSDraftReviewPdf } from "./generator";

const asObject = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return { raw: value };
    }
  }
  if (typeof value === "object") return value;
  return { value };
};

const firstPresent = (...values) =>
  values.find((value) => value != null && value !== "");

export const getSubmissionRecordId = (record = {}) =>
  firstPresent(
    record.id,
    record._id,
    record.submission_id,
    record.submissionId,
    record.history_id,
    record.historyId
  ) || "";

export const extractSubmissionHistoryRecord = (response) => {
  const root = response?.data ?? response;
  if (!root || typeof root !== "object" || Array.isArray(root)) return null;

  const candidates = [
    root.record,
    root.submission,
    root.history,
    root.data?.record,
    root.data?.submission,
    root.data?.history,
    root.data,
    root,
  ];

  return (
    candidates.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        (item.payload ||
          item.request_payload ||
          item.requestPayload ||
          item.response ||
          item.iras_response ||
          item.irasResponse ||
          item.id ||
          item._id ||
          item.submission_id ||
          item.submissionId)
    ) || null
  );
};

const getRecordPayload = (record = {}) =>
  asObject(
    firstPresent(
      record.payload,
      record.request_payload,
      record.requestPayload,
      record.request,
      record.submitted_payload
    )
  ) || {};

const getRecordResponse = (record = {}) =>
  asObject(
    firstPresent(
      record.response,
      record.iras_response,
      record.irasResponse,
      record.response_payload,
      record.responsePayload,
      record.iras_result
    )
  ) || {};

const resolveResponseData = (response) => {
  if (!response || typeof response !== "object") return {};
  if (response.data && typeof response.data === "object") return response.data;
  return response;
};

const getDeclarationAck = (record = {}, payload = {}) =>
  record.internal_declaration_ack ||
  record.internalDeclarationAck ||
  payload.declarationAck ||
  payload.internal_declaration_ack ||
  {};

const sanitizeFilenamePart = (value) =>
  `${value || ""}`
    .trim()
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "") || "record";

const formatSubmissionDateTimeForPdf = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-SG", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export const buildFormCSSubmittedPdfInput = (
  record = {},
  company = {}
) => {
  const payload = getRecordPayload(record);
  const response = getRecordResponse(record);
  const responseData = resolveResponseData(response);
  const nested = responseData?.iras_response?.data || {};
  const filingInfo = payload?.filingInfo || record.filingInfo || {};
  const declarationAck = getDeclarationAck(record, payload);
  const creator =
    record.created_by_user || record.createdByUser || record.creator || {};
  const submissionDate =
    firstPresent(
      record.created_at,
      record.createdAt,
      record.submitted_at,
      record.submittedAt,
      record.updated_at,
      record.updatedAt,
      responseData.submissionDate,
      responseData.submittedAt,
      responseData.timestamp,
      nested.timestamp
    ) || "";
  const acknowledgementNo =
    firstPresent(
      record.acknowledgementNo,
      record.acknowledgement_no,
      record.acknowledgement_number,
      record.ackNo,
      record.ack_no,
      record.iras_acknowledgement_no,
      responseData.acknowledgementNo,
      response.acknowledgementNo,
      responseData.ackNo,
      response.ackNo,
      nested.acknowledgementNo
    ) || "";
  const estTaxPayable = firstPresent(
    record.estTaxPayable,
    record.estimatedTaxPayable,
    record.est_tax_payable,
    responseData.estTaxPayable,
    response.estTaxPayable,
    responseData.estimatedTaxPayable,
    response.estimatedTaxPayable,
    nested.estTaxPayable
  );
  const submittedBy =
    firstPresent(creator.name, creator.email, record.created_by) || "";

  return {
    company: {
      ...company,
      name: company?.name || "",
    },
    formSnapshot: {
      filingInfo,
      dataFormCS: {
        ...(payload?.dataFormCS || {}),
        ...(responseData?.dataFormCS || {}),
      },
      declaration: { ...(payload?.declaration || {}) },
      declarationAck,
    },
    context: {
      documentStatus: "submitted",
      acknowledgementNo: acknowledgementNo ? String(acknowledgementNo) : "",
      estTaxPayable:
        estTaxPayable === "" || estTaxPayable == null
          ? ""
          : String(estTaxPayable),
      summaryRows: [
        ["Submission date", formatSubmissionDateTimeForPdf(submissionDate)],
        ["Submitted by", submittedBy],
      ],
    },
    filename: `${sanitizeFilenamePart(
      filingInfo.uen || company?.uen
    )}_Form-CS_YA${sanitizeFilenamePart(filingInfo.ya)}_submitted.pdf`,
  };
};

export const downloadFormCSSubmittedPdf = ({
  apiResponse,
  fallbackRecord,
  company,
}) => {
  const record =
    extractSubmissionHistoryRecord(apiResponse) || fallbackRecord || {};
  const payload = getRecordPayload(record);
  const response = getRecordResponse(record);

  if (!Object.keys(payload).length && !Object.keys(response).length) {
    throw new Error(
      "This submission does not include Form C-S data to generate a PDF."
    );
  }

  const input = buildFormCSSubmittedPdfInput(record, company);
  const doc = generateFormCSDraftReviewPdf(
    true,
    input.company,
    input.formSnapshot,
    input.context
  );
  doc.save(input.filename);
  return input.filename;
};
